import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Download,
  X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportExcelModal({ open, onClose, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        toast.error("Seleziona un file Excel (.xlsx o .xls)");
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.match(/\.(xlsx|xls)$/)) {
        toast.error("Seleziona un file Excel (.xlsx o .xls)");
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Seleziona un file da importare");
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/tasks/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      
      if (response.data.success) {
        toast.success(`Import completato: ${response.data.created} creati, ${response.data.updated} aggiornati`);
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante l'import";
      toast.error(message);
      setResult({ success: false, error: message });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/tasks/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_tasks.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template scaricato");
    } catch (error) {
      toast.error("Errore nel download del template");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="import-excel-modal-container">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/80 animate-in fade-in-0"
        onClick={handleClose}
      />
      
      {/* Content */}
      <div className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] sm:max-w-[500px] max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] bg-card border border-border p-6 shadow-lg rounded-lg animate-in fade-in-0 zoom-in-95" data-testid="import-excel-modal">
        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
          <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Import Excel
          </h2>
          <p className="text-sm text-muted-foreground">
            Importa task da un file Excel. I task esistenti verranno aggiornati (match per info + sistema + paese).
          </p>
        </div>

        <div className="space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium">Scarica template</p>
              <p className="text-xs text-muted-foreground">Usa il formato corretto per l'import</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
          </div>

          {/* Drop zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            data-testid="drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setResult(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  Trascina qui il file Excel o <span className="text-primary">clicca per selezionare</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supporta .xlsx e .xls
                </p>
              </>
            )}
          </div>

          {/* Import result */}
          {result && (
            <div className={`p-4 rounded-lg border ${
              result.success 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`} data-testid="import-result">
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                )}
                <div className="flex-1">
                  {result.success ? (
                    <>
                      <p className="font-medium text-emerald-500">Import completato!</p>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="flex items-center gap-2">
                          <span className="text-emerald-500">{result.created}</span> task creati
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-blue-500">{result.updated}</span> task aggiornati
                        </p>
                      </div>
                      {result.errors && result.errors.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-500 font-medium">Attenzione:</p>
                          {result.errors.map((err, i) => (
                            <p key={i} className="text-xs text-muted-foreground">{err}</p>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-red-500">Errore nell'import</p>
                      <p className="text-sm text-muted-foreground mt-1">{result.error}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p className="font-medium">Colonne supportate:</p>
            <p>Project, System, Channel, Macro, Country, Priority, Status, Info, Deadline, Completion</p>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 pt-4">
          <Button variant="outline" onClick={handleClose}>
            Chiudi
          </Button>
          <Button onClick={handleImport} disabled={!file || importing} data-testid="import-btn">
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Importa
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
